import * as React from 'react';
import { Button } from '@/components/ui/Button';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { MediaStepCard } from '../MediaStepCard';

const MAX_AVATAR_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

interface ProfileImageSectionProps {
  profileImageUrl: string | null;
  onAvatarSelect: (file: File | null) => void;
  isUploadingAvatar: boolean;
  onContinue: () => void;
  onBack: () => void;
}

/** Profile image upload step with drag-and-drop and preview. */
export const ProfileImageSection: React.FC<ProfileImageSectionProps> = ({
  profileImageUrl,
  onAvatarSelect,
  isUploadingAvatar,
  onContinue,
  onBack,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const dropZoneRef = React.useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const validateAndSelect = (file: File) => {
    setError(null);

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError('Only JPG, PNG, or WEBP images are allowed.');
      return;
    }

    if (file.size > MAX_AVATAR_SIZE) {
      setError('Image must be smaller than 10MB.');
      return;
    }

    onAvatarSelect(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    validateAndSelect(file);
  };

  const handleClearImage = () => {
    onAvatarSelect(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) validateAndSelect(file);
  };

  return (
    <div className='w-full'>
      {/* Header with Back Button */}
      <div className='mb-2 flex items-center gap-3'>
        <button
          type='button'
          onClick={onBack}
          className='rounded-full p-1 transition-colors hover:bg-gray-100'
          aria-label='Go back'
        >
          <ArrowLeftIcon className='size-6 text-dark-100' strokeWidth={2.5} />
        </button>
        <h2 className='font-proxima text-24 font-extrabold uppercase leading-47 text-brand-800 md:text-40'>
          ECHO PROFILE IMAGE
        </h2>
      </div>
      <p className='mb-8 ml-10 font-sans text-16 font-medium leading-27 text-gray-530'>
        Upload a profile photo for your echo.
      </p>
      <MediaStepCard
        title='Add Echo Profile Image'
        subtitle='Recommended: upload a clear, front-facing head-and-shoulders photo. PNG format is preferred, ideally with no background. No animals, flowers or extra objects. Only one person should be visible.'
        error={error}
        footer={
          <Button
            type='button'
            onClick={onContinue}
            variant='purple-dark'
            className='h-[48px] rounded-30 px-10 text-16 font-bold uppercase tracking-wide'
          >
            Continue
          </Button>
        }
      >
        {/* Drop Zone */}
        <div
          ref={dropZoneRef}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={clsx(
            'relative mx-auto h-[223px] w-full overflow-hidden rounded-20 transition-all',
            'border border-dashed border-brand-600 bg-white/40',
            isDragging && 'border-2 border-brand-300 bg-brand-50/30'
          )}
        >
          {profileImageUrl ? (
            <div className='absolute inset-0 bg-white'>
              <img
                src={profileImageUrl}
                alt='Echo profile preview'
                className='size-full object-contain'
              />
              <button
                type='button'
                onClick={handleClearImage}
                className={clsx(
                  'absolute right-3 top-3 flex size-8 items-center justify-center rounded-full',
                  'border-none bg-black/50 text-white backdrop-blur-sm',
                  'cursor-pointer transition-colors hover:bg-black/70'
                )}
              >
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <line x1='18' y1='6' x2='6' y2='18' />
                  <line x1='6' y1='6' x2='18' y2='18' />
                </svg>
              </button>
              {isUploadingAvatar && (
                <div className='absolute inset-0 flex items-center justify-center bg-white/70'>
                  <div className='size-8 animate-spin rounded-full border-4 border-brand-300 border-t-brand-800' />
                </div>
              )}
            </div>
          ) : (
            <button
              type='button'
              onClick={() => fileInputRef.current?.click()}
              className='flex size-full cursor-pointer flex-col items-center justify-center border-none bg-transparent'
            >
              <svg
                width='32'
                height='32'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className='mb-10'
              >
                <path
                  d='M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z'
                  stroke='#45349E'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M14 2V8H20'
                  stroke='#45349E'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>

              <p className='mb-2 font-sans text-20 font-medium leading-[27px] text-dark-300'>
                Drag & Drop or <span className='text-blue-400 underline'>Choose File</span>
              </p>
              <p className='font-sans text-12 font-normal leading-[18px] text-gray-490'>
                Files up to 10MB each
              </p>
            </button>
          )}
        </div>

        <input
          ref={fileInputRef}
          type='file'
          accept='image/jpeg,image/png,image/webp'
          className='hidden'
          onChange={handleFileChange}
        />
      </MediaStepCard>
    </div>
  );
};
